import React, { useMemo, useState } from 'react';
import styled from '@emotion/styled';
import { ArrowLeft, AlertTriangle, Lightbulb, FileText, Sparkles, BookOpen } from 'lucide-react';
import DashboardCharts from './DashboardCharts';
import DashboardPivotCards from './DashboardPivotCards';
import SummaryGroupedListView from './SummaryGroupedListView';
import { hasTagEvaluation } from '../../utils/evaluationDisplay';
import { useTranslation } from '../../hooks/useTranslation.jsx';
import { lensDisplayName } from '../../utils/lensDisplay';

const COLORS = {
  obstacle: '#E94B6E',
  opportunity: '#A6D64D',
};

// ─── Layout ─────────────────────────────────────────────────────────

const Container = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  color: white;
`;

const HeaderBar = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 24px 32px 16px;
  flex-shrink: 0;
`;

const BackButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  color: rgba(255, 255, 255, 0.6);
  font-size: 0.85rem;
  cursor: pointer;
  flex-shrink: 0;
  transition: all 0.15s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.1);
    color: #fff;
  }
`;

const Title = styled.h1`
  margin: 0;
  font-size: 1.3rem;
  font-weight: 700;
  color: #fff;
  flex: 1;
`;

const ScrollArea = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 0 32px 48px;
`;

const ContentWrapper = styled.div`
  max-width: ${p => (p.$wide ? '1320px' : '960px')};
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 28px;
`;

// ─── Stats Bar ──────────────────────────────────────────────────────

const StatsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
`;

const StatPill = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  border-radius: 10px;
  font-size: 0.82rem;
  font-weight: 600;
  background: ${p => p.$bg || 'rgba(255, 255, 255, 0.06)'};
  color: ${p => p.$color || 'rgba(255, 255, 255, 0.7)'};
  border: 1px solid ${p => p.$border || 'rgba(255, 255, 255, 0.08)'};
`;

const Divider = styled.div`
  width: 1px;
  height: 28px;
  background: rgba(255, 255, 255, 0.08);
  align-self: center;
`;

// ─── Tabs ──────────────────────────────────────────────────────────

const TabRow = styled.div`
  display: flex;
  gap: 4px;
  padding: 0 32px 8px;
  flex-shrink: 0;
`;

const Tab = styled.button`
  padding: 6px 14px;
  border-radius: 8px;
  border: none;
  background: ${p => p.$active ? 'rgba(255, 255, 255, 0.12)' : 'transparent'};
  color: ${p => p.$active ? '#fff' : 'rgba(255, 255, 255, 0.4)'};
  font-size: 0.8rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.08);
  }
`;

// ─── Empty State ────────────────────────────────────────────────────

const EmptyState = styled.div`
  text-align: center;
  padding: 64px 24px;
  color: rgba(255, 255, 255, 0.3);
  font-size: 0.95rem;
`;

// ─── Helpers ────────────────────────────────────────────────────────

function avg(arr) {
  if (arr.length === 0) return 0;
  return Math.round(arr.reduce((s, v) => s + v, 0) / arr.length);
}

/**
 * LensDashboard — Cross-pivot aggregated analysis dashboard.
 *
 * Shows stats bar, pivot×lens heatmap, lens distribution chart,
 * and per-pivot collapsible drill-down cards.
 */
export default function LensDashboard({
  items = [],
  lenses = [],
  pivotPoints = [],
  pivotEdits = {},
  onBack,
  onPivotNavigate,
  showDescription = true,
}) {
  const { t, language } = useTranslation();

  // ─── Aggregated Stats ───────────────────────────────────────────

  const stats = useMemo(() => {
    const obstacles = items.filter(i => i.type === 'obstacle');
    const opportunities = items.filter(i => i.type === 'opportunity');

    const manual = items.filter(i => i.source === 'manual').length;
    const ai = items.filter(i => i.source === 'ai').length;
    const rag = items.filter(i => i.source === 'rag').length;

    // Extract scores from items that have them
    const qualityScores = [];
    const researchScores = [];
    items.forEach(item => {
      const scores = item.rag_references?.scores;
      if (scores) {
        if (typeof scores.quality === 'number') qualityScores.push(scores.quality);
        if (typeof scores.research_support === 'number') researchScores.push(scores.research_support);
      }
    });

    const taggedCount = items.filter((item) => hasTagEvaluation(item.rag_references?.scores)).length;

    return {
      total: items.length,
      obstacles: obstacles.length,
      opportunities: opportunities.length,
      manual,
      ai,
      rag,
      avgQuality: avg(qualityScores),
      avgResearch: avg(researchScores),
      scoredCount: qualityScores.length,
      taggedCount,
    };
  }, [items]);

  // ─── Pivot-lens matrix data ─────────────────────────────────────

  const matrixData = useMemo(() => {
    // { pivotId: { lensId: { obstacles: N, opportunities: N } } }
    const matrix = {};
    items.forEach(item => {
      if (!matrix[item.pivot_point_id]) matrix[item.pivot_point_id] = {};
      if (!matrix[item.pivot_point_id][item.lens_id]) {
        matrix[item.pivot_point_id][item.lens_id] = { obstacles: 0, opportunities: 0 };
      }
      if (item.type === 'obstacle') matrix[item.pivot_point_id][item.lens_id].obstacles++;
      else matrix[item.pivot_point_id][item.lens_id].opportunities++;
    });
    return matrix;
  }, [items]);

  // ─── Lens distribution data (for bar chart) ────────────────────

  const lensDistribution = useMemo(() => {
    const sortedLenses = [...lenses].sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));
    return sortedLenses.map(lens => {
      const lensItems = items.filter(i => i.lens_id === lens.id);
      const label = lensDisplayName(lens, language);
      return {
        // name_en when the workshop runs in English — see utils/lensDisplay
        name: label.length > 12 ? label.substring(0, 12) + '…' : label,
        fullName: label,
        color: lens.color,
        obstacles: lensItems.filter(i => i.type === 'obstacle').length,
        opportunities: lensItems.filter(i => i.type === 'opportunity').length,
      };
    });
  }, [items, lenses, language]);

  // ─── Per-pivot grouped items ────────────────────────────────────

  const pivotGroups = useMemo(() => {
    // Collect unique pivot IDs from items
    const pivotIds = [...new Set(items.map(i => i.pivot_point_id))];
    return pivotIds.map(pivotId => {
      const pp = pivotPoints.find(p => p.id === pivotId);
      const edit = pivotEdits[pivotId];
      const pivotItems = items.filter(i => i.pivot_point_id === pivotId);

      const qualityScores = [];
      pivotItems.forEach(item => {
        const q = item.rag_references?.scores?.quality;
        if (typeof q === 'number') qualityScores.push(q);
      });

      return {
        pivotId,
        title: edit?.title_override || pp?.title || t('lenses.unknownPivot'),
        description: edit?.description_override || pp?.description || '',
        items: pivotItems,
        obstacles: pivotItems.filter(i => i.type === 'obstacle').length,
        opportunities: pivotItems.filter(i => i.type === 'opportunity').length,
        avgQuality: avg(qualityScores),
      };
    });
  }, [items, pivotPoints, pivotEdits, t]);

  // ─── Sorted lenses for heatmap ─────────────────────────────────

  const sortedLenses = useMemo(() =>
    [...lenses].sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0)),
    [lenses]
  );

  // ─── Tabs ──────────────────────────────────────────────────────

  // Land on by-pivot listing (mirrors Solutions Yhteenveto)
  const [activeTab, setActiveTab] = useState('pivots');

  const tabIsWide = activeTab === 'listing';

  // ─── Render ────────────────────────────────────────────────────

  return (
    <Container>
      <HeaderBar>
        {onBack && (
          <BackButton onClick={onBack}>
            <ArrowLeft size={16} />
            {t('common.back')}
          </BackButton>
        )}
        <Title>{t('lenses.tabSummary')}</Title>
      </HeaderBar>

      <TabRow>
        <Tab $active={activeTab === 'dashboard'} onClick={() => setActiveTab('dashboard')}>
          {t('lenses.tabSummary')}
        </Tab>
        <Tab $active={activeTab === 'pivots'} onClick={() => setActiveTab('pivots')}>
          {t('lenses.tabPivots', { count: pivotGroups.length })}
        </Tab>
        <Tab $active={activeTab === 'listing'} onClick={() => setActiveTab('listing')}>
          {t('lenses.tabListing')}
        </Tab>
      </TabRow>

      <ScrollArea>
        <ContentWrapper $wide={tabIsWide}>
          {activeTab === 'dashboard' && (
            <>
              {/* Stats Bar */}
              <StatsRow>
                <StatPill>
                  {t('lenses.itemsCount', { count: stats.total })}
                </StatPill>
                <StatPill $bg={`${COLORS.obstacle}15`} $color={COLORS.obstacle} $border={`${COLORS.obstacle}30`}>
                  <AlertTriangle size={14} />
                  {stats.obstacles}{' '}
                  {stats.obstacles === 1 ? t('lenses.obstacleOne') : t('lenses.obstacleOther')}
                </StatPill>
                <StatPill $bg={`${COLORS.opportunity}15`} $color={COLORS.opportunity} $border={`${COLORS.opportunity}30`}>
                  <Lightbulb size={14} />
                  {stats.opportunities}{' '}
                  {stats.opportunities === 1 ? t('lenses.opportunityOne') : t('lenses.opportunityOther')}
                </StatPill>

                <Divider />

                <StatPill>
                  <FileText size={13} />
                  {t('lenses.manualCount', { count: stats.manual })}
                </StatPill>
                <StatPill $bg="rgba(156,39,176,0.1)" $color="#9C27B0" $border="rgba(156,39,176,0.2)">
                  <Sparkles size={13} />
                  {t('lenses.aiCount', { count: stats.ai })}
                </StatPill>
                {stats.rag > 0 && (
                  <StatPill $bg="rgba(33,150,243,0.1)" $color="#2196F3" $border="rgba(33,150,243,0.2)">
                    <BookOpen size={13} />
                    {t('lenses.ragCount', { count: stats.rag })}
                  </StatPill>
                )}

                {stats.taggedCount > 0 && (
                  <>
                    <Divider />
                    <StatPill>{t('lenses.evaluatedTags', { count: stats.taggedCount })}</StatPill>
                  </>
                )}
                {stats.scoredCount > 0 && stats.taggedCount === 0 && (
                  <>
                    <Divider />
                    <StatPill>
                      {t('lenses.qualityScore', { score: stats.avgQuality })}
                    </StatPill>
                    <StatPill>
                      {t('lenses.researchSupport', {
                        score: `${stats.avgResearch > 0 ? '+' : ''}${stats.avgResearch}`,
                      })}
                    </StatPill>
                  </>
                )}
              </StatsRow>

              {items.length === 0 ? (
                <EmptyState>
                  {t('lenses.emptyItems')}
                </EmptyState>
              ) : (
                <DashboardCharts
                  matrixData={matrixData}
                  lensDistribution={lensDistribution}
                  sortedLenses={sortedLenses}
                  pivotGroups={pivotGroups}
                />
              )}
            </>
          )}

          {activeTab === 'pivots' && (
            <DashboardPivotCards
              pivotGroups={pivotGroups}
              onPivotClick={onPivotNavigate}
            />
          )}

          {activeTab === 'listing' && (
            <SummaryGroupedListView
              items={items}
              lenses={sortedLenses}
              pivotPoints={pivotPoints}
              pivotEdits={pivotEdits}
              showDescription={showDescription}
            />
          )}
        </ContentWrapper>
      </ScrollArea>
    </Container>
  );
}
